// FLAGS: legacy, silent-failure
// Provider shell and the single React Query client for the whole dashboard. Two of the options set on that client
// do not exist in React Query v5 and are silently ignored, so the suspense behaviour the comments below describe
// does not happen — see docs/architecture-admin.md before trusting any loading state in this app.
import { ThemeProvider, Theme, StyledEngineProvider } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useRoutes } from "react-router-dom";
import { env } from "@src/env";
import {
  AuthenticationProvider,
  useAuthentication,
} from "@src/components/wrappers/AuthenticationProvider";
import { setupAxiosInterceptors } from "@src/utils/axiosInterceptors";
import theme from "./theme";
import routes from "./routes";
import { consultStaleTime } from "./queries/staletimes";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: true, // Throw errors so we can use catch them using QueryErrorResetBoundary in the withSuspense
      suspense: true, // Suspend query while loading so we can display Loader using withSuspense
      keepPreviousData: true,
      refetchOnWindowFocus: env.REACT_APP_HOSTING_ENV === "production",
      retry: false,
      staleTime: consultStaleTime,
    },
  },
});

const Routes = () => {
  const { isLoggedIn, hasRole } = useAuthentication();

  return useRoutes(routes(isLoggedIn, hasRole));
};

// Initialize interceptors once at module load
setupAxiosInterceptors();

const Wrapped = () => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <GoogleOAuthProvider clientId={env.REACT_APP_GOOGLE_CLIENT_ID}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <QueryClientProvider client={queryClient}>
            <AuthenticationProvider>
              <Routes />
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthenticationProvider>
          </QueryClientProvider>
        </LocalizationProvider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);

export default Wrapped;
